import React, { useMemo } from 'react';
import './IngredientsSection.css';

function IngredientsSection({ baseRecipe, quantity }) {
  // Calculate ingredients based on quantity
  const ingredients = useMemo(() => {
    // Helper function to convert decimal cups to standard measurements
    // Moved inside useMemo to avoid dependency issues
    const formatCupMeasurement = (cups) => {
      // Handle specific known quantities
      if (quantity === 12 && cups === 1) {
        return '1'; // Explicitly return '1' for milk when quantity is 12
      }
      
      const cupValue = parseFloat(cups);
      
      // Map of standard cup fractions and their decimal values
      const standardCups = [
        { fraction: '1/8', decimal: 0.125 },
        { fraction: '1/4', decimal: 0.25 },
        { fraction: '1/3', decimal: 0.333 },
        { fraction: '1/2', decimal: 0.5 },
        { fraction: '2/3', decimal: 0.667 },
        { fraction: '3/4', decimal: 0.75 },
        { fraction: '1', decimal: 1 }
      ];
      
      // For whole number of cups
      const wholeCups = Math.floor(cupValue);
      const fraction = cupValue - wholeCups;
      
      // Find closest standard fraction
      let closestFraction = '';
      let minDiff = 1;
      
      for (const cup of standardCups) {
        const diff = Math.abs(fraction - cup.decimal);
        if (diff < minDiff) {
          minDiff = diff;
          closestFraction = cup.fraction;
        }
      }
      
      // Format the result
      if (wholeCups > 0) {
        // If we have both whole cups and a fraction
        if (closestFraction !== '1' && closestFraction !== '') {
          return `${wholeCups} ${closestFraction}`;
        }
        // If it's a whole number or we rounded to a whole number
        return `${wholeCups + (closestFraction === '1' ? 1 : 0)}`;
      } else {
        // Just the fraction
        return closestFraction;
      }
    };

    const multiplier = quantity / 6; // Base recipe is for 6 pavs
    
    // Split sugar between A and B (half and half)
    const totalSugar = baseRecipe.sugar * multiplier;
    
    // Calculate raw values
    const rawValues = {
      flour: baseRecipe.flour * multiplier,
      milk: baseRecipe.milk * multiplier,
      sugarA: totalSugar / 2,
      sugarB: totalSugar / 2,
      egg: baseRecipe.egg * multiplier,
      butter: baseRecipe.butter * multiplier,
      yeast: baseRecipe.yeast * multiplier,
      salt: baseRecipe.salt * multiplier
    };
    
    // Format yeast display
    let yeastDisplay;
    let yeastUnit;
    
    // Always display yeast in teaspoons as that's the base unit
    yeastDisplay = rawValues.yeast.toFixed(1);
    yeastUnit = "teaspoons";
    
    // For common measurements, provide more user-friendly values
    if (Math.abs(rawValues.yeast - 2.25) < 0.1) {
      // This is equivalent to 1 packet, so add that as a note
      yeastUnit = "teaspoons (1 packet)";
    }
    
    // Format measurements
    return {
      flour: formatCupMeasurement(rawValues.flour),
      milk: formatCupMeasurement(rawValues.milk),
      sugarA: formatCupMeasurement(rawValues.sugarA),
      sugarB: formatCupMeasurement(rawValues.sugarB),
      egg: rawValues.egg.toFixed(1),
      butter: rawValues.butter.toFixed(1),
      yeast: yeastDisplay,
      yeastUnit: yeastUnit,
      salt: rawValues.salt.toFixed(1)
    };
  }, [baseRecipe, quantity]);

  return (
    <section className="ingredients-section">
      <h2>Ingredients</h2>
      <p className="measurement-note">* All measurements are converted to standard cup sizes (1/8, 1/4, 1/3, 1/2, 2/3, 3/4, 1 cup)</p>
      <div className="ingredient-groups">
        <div className="ingredient-group">
          <h3>Ingredients A</h3>
          <table>
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Quantity</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Whole Milk</td>
                <td>{ingredients.milk}</td>
                <td>cup</td>
              </tr>
              <tr>
                <td>Sugar</td>
                <td>{ingredients.sugarA}</td>
                <td>cup</td>
              </tr>
              <tr>
                <td>Yeast</td>
                <td>{ingredients.yeast}</td>
                <td>{ingredients.yeastUnit}</td>
              </tr>
            </tbody>
          </table>
          <p className="note">Lukewarm milk + half of sugar then add yeast if you can comfortably put fingers in milk. Put away for 5 min, it should froth, throw it away and start again if it doesn't froth.</p>
        </div>

        <div className="ingredient-group">
          <h3>Ingredients B</h3>
          <table>
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Quantity</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Bread Flour (12.7% Protein content)</td>
                <td>{ingredients.flour}</td>
                <td>cup</td>
              </tr>
              <tr>
                <td>Remaining Sugar</td>
                <td>{ingredients.sugarB}</td>
                <td>cup</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="ingredient-group">
          <h3>Ingredients C</h3>
          <table>
            <thead>
              <tr>
                <th>Ingredient</th>
                <th>Quantity</th>
                <th>Unit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Whole Egg (optional but recommended)</td>
                <td>{ingredients.egg}</td>
                <td></td>
              </tr>
              <tr>
                <td>Salt</td>
                <td>{ingredients.salt}</td>
                <td>tsp</td>
              </tr>
              <tr>
                <td>Butter</td>
                <td>{ingredients.butter}</td>
                <td>Tbsp</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

export default IngredientsSection;